import React, { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, ArrowRight, Loader2, Mail, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { SCENARIO_GUIDE_URL } from "@/components/SystemStrip";

const PRODUCT_NAMES = {
  part_1: "Toolkit 1 — The Role",
  part_2: "Toolkit 2 — The People",
  part_3: "Toolkit 3 — The Moments",
  bundle: "All 3 toolkits — the full system",
  team_5: "Team License · 5 seats", team_10: "Team License · 10 seats", team_15: "Team License · 15 seats",
  team_20: "Team License · 20 seats", team_25: "Team License · 25 seats",
};

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { applyAuth } = useAuth();
  const [status, setStatus] = useState("checking"); // checking | paid | failed
  const [info, setInfo] = useState({});
  const [password, setPassword] = useState("");
  const [mode, setMode] = useState("password"); // password | link
  const [loading, setLoading] = useState(false);
  const [linkSent, setLinkSent] = useState(false);
  const [authed, setAuthed] = useState(false);
  const tries = useRef(0);
  const opened = useRef(false);

  const isTeam = String(info.lookup_key || "").startsWith("team_");
  const wantsMoments = ["part_3", "bundle"].includes(info.lookup_key) || isTeam;
  const ready = status === "paid" && (!info.needs_setup || authed);

  // Auto-open the Scenario Guide once access is confirmed for a Moments/bundle purchase.
  useEffect(() => {
    if (ready && wantsMoments && !opened.current) {
      opened.current = true;
      window.open(SCENARIO_GUIDE_URL, "_blank", "noopener,noreferrer");
    }
  }, [ready, wantsMoments]);

  useEffect(() => {
    if (!sessionId) { setStatus("failed"); return; }
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        setInfo(data);
        if (data.payment_status === "paid") { setStatus("paid"); return; }
        if (["failed", "expired"].includes(data.payment_status)) { setStatus("failed"); return; }
      } catch { /* keep polling */ }
      tries.current += 1;
      if (tries.current > 12) { setStatus("failed"); return; }
      timer = setTimeout(poll, 1800);
    };
    poll();
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const setupPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/payments/claim/set-password", { session_id: sessionId, password });
      applyAuth(data);
      toast.success("Account created — you're in.");
      setAuthed(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  const sendLink = async () => {
    setLoading(true);
    try {
      await api.post("/auth/magic-link/request", { email: info.buyer_email });
      setLinkSent(true);
      toast.success("Sign-in link sent — check your email.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  const productName = PRODUCT_NAMES[info.lookup_key] || "Your purchase";

  return (
    <div className="min-h-screen bg-nm-bg nm-grain font-sans flex items-center justify-center px-6 py-14">
      <div className="w-full max-w-md rounded-3xl border border-nm-primary/12 bg-nm-surface p-10 nm-shadow-md text-center" data-testid="payment-success-page">
        {status === "checking" && (
          <>
            <Loader2 size={44} className="mx-auto text-nm-primary animate-spin" />
            <h1 className="font-serif text-3xl text-nm-ink mt-5">Confirming your purchase…</h1>
            <p className="text-nm-neutral mt-2">This only takes a moment.</p>
          </>
        )}

        {status === "paid" && (
          <>
            <div className="mx-auto h-16 w-16 rounded-full bg-nm-green/15 text-nm-green flex items-center justify-center"><CheckCircle2 size={34} /></div>
            <h1 className="font-serif text-3xl text-nm-ink mt-5" data-testid="payment-paid">Payment received.</h1>
            <p className="text-nm-neutral mt-2">{productName} is yours.</p>

            {/* Access is ready (logged-in purchase, or after account setup) */}
            {ready && (
              wantsMoments ? (
                <div className="mt-6" data-testid="moments-handoff">
                  <p className="text-sm text-nm-ink/85 leading-relaxed">Your Scenario Guide is opening in a new tab. If it didn't, use the button below.</p>
                  <a href={SCENARIO_GUIDE_URL} target="_blank" rel="noopener noreferrer" data-testid="open-scenario-guide"
                    className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#C2952B] px-7 py-3.5 text-sm font-semibold text-white hover:brightness-105 transition-[filter] duration-200">
                    Open the Scenario Guide <ExternalLink size={16} />
                  </a>
                  <Link to="/app" data-testid="goto-app" className="mt-3 w-full inline-flex items-center justify-center gap-2 rounded-full border border-nm-primary/25 px-7 py-3 text-sm font-medium text-nm-primary hover:bg-nm-primary/5 transition-colors duration-200">
                    Go to your dashboard <ArrowRight size={15} />
                  </Link>
                  {isTeam && (
                    <Link to="/app/team" data-testid="goto-team" className="mt-3 w-full inline-flex items-center justify-center gap-2 rounded-full bg-nm-primary px-7 py-3 text-sm font-semibold text-white hover:brightness-105 transition-[filter] duration-200">
                      Add your team <ArrowRight size={15} />
                    </Link>
                  )}
                </div>
              ) : (
                <Link to="/app" className="mt-7 inline-flex items-center gap-2 rounded-full bg-nm-coral px-7 py-3.5 text-sm font-medium text-white hover:brightness-105 transition-[filter] duration-200" data-testid="goto-app">
                  Open the toolkit <ArrowRight size={16} />
                </Link>
              )
            )}

            {/* Guest purchase, email already has an account */}
            {!ready && info.needs_setup && info.account_status === "existing" && (
              <div className="mt-6 text-left">
                <p className="text-sm text-nm-ink/85 leading-relaxed">
                  We found an account for <span className="font-medium">{info.buyer_email}</span> and added this purchase to it. We've emailed a one-click sign-in link{wantsMoments ? " — sign in to open your Scenario Guide." : "."}
                </p>
                <div className="mt-5 flex flex-col gap-3">
                  <button onClick={sendLink} disabled={loading} data-testid="resend-link"
                    className="inline-flex items-center justify-center gap-2 rounded-full border border-nm-primary/25 px-6 py-3 text-sm font-medium text-nm-primary hover:bg-nm-primary/5 transition-colors duration-200">
                    <Mail size={16} /> {loading ? "Sending…" : "Resend sign-in link"}
                  </button>
                  <Link to="/login" data-testid="goto-login" className="inline-flex items-center justify-center gap-2 rounded-full bg-nm-coral px-6 py-3 text-sm font-medium text-white hover:brightness-105 transition-[filter] duration-200">
                    Sign in <ArrowRight size={15} />
                  </Link>
                </div>
              </div>
            )}

            {/* Guest purchase, brand-new account — set up access */}
            {!ready && info.needs_setup && info.account_status !== "existing" && (
              <div className="mt-6 text-left">
                <h2 className="font-serif text-lg text-nm-ink text-center">Set up your access</h2>
                <p className="text-sm text-nm-neutral text-center mt-1">for {info.buyer_email || "your account"}</p>

                {mode === "password" ? (
                  <form onSubmit={setupPassword} className="mt-5 space-y-3">
                    <label className="text-sm text-nm-ink font-medium">Choose a password</label>
                    <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                      className="w-full rounded-xl border border-nm-primary/20 bg-white px-4 py-3 text-sm outline-none focus:border-nm-primary"
                      placeholder="At least 6 characters" data-testid="setup-password" />
                    <button type="submit" disabled={loading} data-testid="setup-password-submit"
                      className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-nm-coral px-6 py-3 text-sm font-medium text-white hover:brightness-105 transition-[filter] duration-200">
                      {loading ? "Setting up…" : <>Create account & open toolkit <ArrowRight size={15} /></>}
                    </button>
                    <button type="button" onClick={() => setMode("link")} data-testid="switch-to-link"
                      className="w-full text-center text-sm text-nm-primary font-medium hover:underline">
                      Email me a sign-in link instead
                    </button>
                  </form>
                ) : (
                  <div className="mt-5 space-y-3">
                    {linkSent ? (
                      <p className="text-sm text-nm-ink/85 text-center leading-relaxed">
                        Check <span className="font-medium">{info.buyer_email}</span> for a one-click sign-in link. It unlocks your purchase.
                      </p>
                    ) : (
                      <p className="text-sm text-nm-neutral text-center">We'll email a one-click sign-in link to {info.buyer_email}. No password needed.</p>
                    )}
                    <button onClick={sendLink} disabled={loading} data-testid="send-link"
                      className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-nm-coral px-6 py-3 text-sm font-medium text-white hover:brightness-105 transition-[filter] duration-200">
                      <Mail size={16} /> {loading ? "Sending…" : (linkSent ? "Resend link" : "Send my sign-in link")}
                    </button>
                    <button type="button" onClick={() => setMode("password")} data-testid="switch-to-password"
                      className="w-full text-center text-sm text-nm-primary font-medium hover:underline">
                      Set a password instead
                    </button>
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {status === "failed" && (
          <>
            <div className="mx-auto h-16 w-16 rounded-full bg-nm-coral/15 text-nm-coral flex items-center justify-center"><XCircle size={34} /></div>
            <h1 className="font-serif text-3xl text-nm-ink mt-5">We couldn't confirm payment</h1>
            <p className="text-nm-neutral mt-2">If you were charged, your access will appear shortly. Otherwise, please try again.</p>
            <Link to="/#pricing" className="mt-7 inline-flex items-center gap-2 rounded-full bg-nm-primary px-7 py-3.5 text-sm font-medium text-white hover:brightness-105 transition-[filter] duration-200" data-testid="retry-purchase">
              Back to pricing
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
